import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import Icon from '../../../components/icon/Icon';
import InteractiveElement from '../../../components/interactive-element/InteractiveElement';
import { FILE_LOCATION, IMG_LOCATION } from '../../../core/constants/api';
import { getProjectDetails, getProjectFile } from '../../../core/state/project/asyncActions';
import { RootState } from '../../../core/state/state';
import CollapsiblePanel from '../../components/collapsible-panel/CollapsiblePanel';
import Button from '../../../components/form/button/Button';
import ProjectDetailsContact from './ProjectDetailsContact';
import Image from '../../../components/image/Image';
import { DATE_FORMAT_EU_DOT } from '../../../core/constants/date';
import Tooltip from '../../../components/tooltip/Tooltip';
import { spawnGlobalNotification } from '../../../core/state/global-notifications/asyncActions';
import { NotificationPosition } from '../../../core/state/global-notifications/types';
import { GlobalNotificationTypes } from '../../../core/enums/GlobalNotificationTypes';
import { isMobileScreenSize } from '../../../core/utils/isMobileScreenSize';
import { File } from '../../../core/state/project/types';

import './ProjectDetails.scss';

const cls = 'project-details__info';

const ProjectDetails: React.FC<{ projectId: number }> = ({ projectId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const project = useSelector((state: RootState) => state.projects.projectDetails);
  const isMobileScreen = isMobileScreenSize();

  React.useEffect(() => {
    if (projectId) {
      dispatch(getProjectDetails(Number(projectId)));
    }
  }, [dispatch, projectId]);

  const downloadFileButton = (document: File) => (
    <Button
      onClick={async () => {
        await dispatch(getProjectFile(projectId, document.id));
        dispatch(
          spawnGlobalNotification({
            postition: NotificationPosition.Top,
            type: GlobalNotificationTypes.Success,
            title: t('projectSettings.documents.notification.fileDownloaded.title'),
            message: `${t('projectSettings.documents.notification.fileDownloaded.text')} ${document.name}`
          })
        );
      }}
      buttonTypes={['blue', `${isMobileScreen ? 'block' : 'extra-small'}`]}
    >
      {isMobileScreen && t('common.downloadFile')}
      <Tooltip placement="top" text={t('common.downloadFile')}>
        <Icon className={`${cls}-item-file-download`} icon="download" svg />
      </Tooltip>
    </Button>
  );

  const logisticPlans = project?.projectPlanDocuments ?? [];

  return (
    <CollapsiblePanel title={t('projectDetails.header.title')}>
      {project && (
        <div className={`project-details ${isMobileScreen ? 'project-details--mobile' : ''}`}>
          <div className="project-details__header">
            <Image className="project-details__header-project-img" src={`${IMG_LOCATION}/${project?.image.fullName}`} alt={project?.image.name} />
            <div className="d-flex flex-column">
              <span className="project-details__header-project-name">{project.name}</span>
              <div className={`d-flex mt-1 ${isMobileScreen && 'flex-column-reverse'}`}>
                <span className="project-details__header-project-number">{project?.code}</span>
                <span className="project-details__header-project-area">{project.constructionArea}</span>
              </div>
            </div>
          </div>
          <div className="project-details__grid">
            <div className={`${cls}`}>
              <div className={`${cls}-item`}>
                <span className={`${cls}-title`}>{t('projectDetails.title.contructionAddress')}</span>
                <span className={`${cls}-text`}>{project?.constructionSite.address}</span>
              </div>
              <div className={`${cls}-item`}>
                <span className={`${cls}-title`}>{t('projectDetails.title.contact')}</span>
                <div className="d-flex flex-column">
                  <ProjectDetailsContact project={project} />
                </div>
              </div>
            </div>
            <div className={`${cls}`}>
              <div className={`${cls}-item`}>
                <span className={`${cls}-title`}>{t('projectDetails.title.info')}</span>
                <span className={`${cls}-text`}>{project?.constructionSite.deliveryNotes}</span>
              </div>
              {logisticPlans.length > 0 ? (
                <div className={`${cls}-item`}>
                  <span className={`${cls}-title`}>{t('projectDetails.title.file')}</span>
                  <div>
                    {logisticPlans.map((document) => (
                      <div className="mb-2" key={document.id}>
                        <div className={`${cls}-item-file`}>
                          <Icon className={`${cls}-item-file-icon`} icon="file" svg />
                          <div className={`${cls}-item-file-data`}>
                            <div className="d-flex flex-column">
                              <InteractiveElement
                                onClick={!isMobileScreen ? () => window.open(`${FILE_LOCATION}/${document.fullName}`, '_file') : () => {}}
                              >
                                <Tooltip placement="top" text={t('common.openFile')}>
                                  <b>{document.name}</b>
                                </Tooltip>
                              </InteractiveElement>
                              <p className={`${cls}-item-file-owner`}>
                                {`${t('projectDetails.text.uploadedOn')} ${format(new Date(document.createdOn), DATE_FORMAT_EU_DOT)} ${t(
                                  'common.by'
                                )} `}
                                <span className={`${cls}-item-file-owner-name`}>{document.createdBy}</span>
                              </p>
                            </div>
                            {!isMobileScreen && downloadFileButton(document)}
                          </div>
                        </div>
                        {isMobileScreen && downloadFileButton(document)}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={`${cls}-item`}>
                  <span className={`${cls}-title`}>{t('projectDetails.title.file')}</span>
                  <div className={`${cls}-item`}>
                    <span className={`${cls}-text`}>{t('projectDetails.title.noPlanAvailable')}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </CollapsiblePanel>
  );
};

export default ProjectDetails;
